// AhorrosCli.jsx  —  Visión cliente (solo clínicas matriz)
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Building2, TrendingDown, Package, AlertCircle, BarChart2,
  DollarSign, Percent, ArrowDown, ArrowUp, Info, History,
  Users, Calendar, ShoppingCart, Tag, TrendingUp, Award,
  Filter, CheckCircle,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Cell, LineChart, Line, Legend,
} from 'recharts';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import SearchInput from '../components/SearchInput';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (v == null || v === '' || isNaN(v)) return '—';
  const n = parseFloat(v);
  return n.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};
const fmtBigMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = parseFloat(v);
  return n >= 1000 ? `${(n / 1000).toFixed(2)}k €` : `${n.toFixed(2)} €`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m] = String(d).split('-');
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return m ? `${meses[parseInt(m) - 1]} ${y}` : d;
};

const RANGO_COLORS = {
  'Sin configurar': '#9ca3af', '0% o menos': '#ef4444',
  '1% - 10%': '#f59e0b',      '11% - 20%': '#3cbf9a',
  '21% - 30%': '#059669',     'Más del 30%': '#047857',
};

// ─── Acceso denegado ─────────────────────────────────────────────────────────
function AccesoDenegado() {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" style={{ top: 64 }}>
      <div className="bg-white rounded-2xl shadow-lg p-12 border border-gray-100 text-center max-w-md">
        <div className="p-6 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-amber-100">
          <span className="text-4xl">🔒</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Sección no disponible</h2>
        <p className="text-gray-600 leading-relaxed">
          El módulo de Análisis de Ahorros solo está disponible para clínicas matriz.
          Si crees que deberías tener acceso, contacta con tu administrador.
        </p>
      </div>
    </div>
  );
}

// ─── Tooltip Recharts ────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || '#fff' }}>
          {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
export default function AhorrosCli() {
  const { user }    = useContext(AuthContext);
  const { theme }   = useTheme();

  // Colores del tema
  const P     = theme.primary;
  const P_LT  = theme.primaryLight;
  const P_DK  = theme.primaryDark;
  const P_BD  = theme.primaryBorder;

  // ── Estado de carga y acceso ─────────────────────────────────────────────
  const [loading, setLoading]       = useState(true);
  const [accessDenied, setAccess]   = useState(false);  // usuario no es matriz
  const [error, setError]           = useState(null);

  // ── Grupo de clínicas ────────────────────────────────────────────────────
  const [matrizId, setMatrizId]       = useState(null);
  const [matrizInfo, setMatrizInfo]   = useState(null);
  const [clientes, setClientes]       = useState([]);   // grupo completo

  // ── Datos de las pestañas ────────────────────────────────────────────────
  const [productos, setProductos]         = useState([]);
  const [estadisticas, setEstadisticas]   = useState(null);
  const [historicoData, setHistoricoData] = useState(null);

  const [loadingProds, setLoadingProds]         = useState(false);
  const [loadingStats, setLoadingStats]         = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  // ── UI ───────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]             = useState('precios');
  const [searchFilter, setSearchFilter]       = useState('');

  const [histSearch, setHistSearch]           = useState('');
  const [histOrden, setHistOrden]             = useState('ahorro_total');
  const [clientesSel, setClientesSel]         = useState(new Set());
  const [histFiltroProveedor, setHistProveedor] = useState('');
  const [histFechaDesde, setHistFechaDesde]     = useState('');
  const [histFechaHasta, setHistFechaHasta]     = useState('');
  const [proveedoresDisponibles, setProvDisp]   = useState([]);

  const handleClienteToggle = (id) =>
    setClientesSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── 1. Carga inicial: determinar matriz del usuario ──────────────────────
  useEffect(() => {
    window.scrollTo(0, 0);
    initCliente();
  }, []);

  const initCliente = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_URL}/api/clientes/disponibles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar los datos de la clínica');
      const data = await res.json();

      const principal = data.agrupadas?.principal;

      // Solo puede acceder si el usuario es una clínica MATRIZ
      // (clinica_matriz === 0 o null significa que ES la matriz)
      if (!principal?.matriz) {
        setAccess(true);
        return;
      }

      const matriz = principal.matriz;
      const esMatriz = parseInt(matriz.clinica_matriz) === 0 || matriz.clinica_matriz === null;

      if (!esMatriz) {
        // El usuario es una clínica asociada, no la matriz → sin acceso
        setAccess(true);
        return;
      }

      setMatrizId(matriz.id);
      setMatrizInfo(matriz);

      // Grupo completo (matriz + asociadas)
      const grupo = [matriz, ...(principal.asociadas || [])];
      setClientes(grupo);

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── 2. Cuando tenemos matrizId, cargar datos de la primera pestaña ───────
  useEffect(() => {
    if (!matrizId) return;
    loadProductos(matrizId);
    loadEstadisticas(matrizId);
  }, [matrizId]);

  const loadProductos = async (mId) => {
    try {
      setLoadingProds(true);
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_URL}/api/ahorros/${mId}/productos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setProductos(d.productos || []);
    } catch { setProductos([]); }
    finally   { setLoadingProds(false); }
  };

  const loadEstadisticas = async (mId) => {
    try {
      setLoadingStats(true);
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_URL}/api/ahorros/${mId}/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      setEstadisticas(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingStats(false); }
  };

  const loadHistorico = useCallback(async (mId, selIds, proveedor, fechaDesde, fechaHasta) => {
    try {
      setLoadingHistorico(true);
      const p = new URLSearchParams();
      if (selIds?.size > 0) p.set('clientes',    [...selIds].join(','));
      if (proveedor)        p.set('proveedor',   proveedor);
      if (fechaDesde)       p.set('fecha_desde', fechaDesde);
      if (fechaHasta)       p.set('fecha_hasta', fechaHasta);
      const qs = p.toString() ? `?${p.toString()}` : '';
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_URL}/api/ahorros/${mId}/ahorro-historico${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setHistoricoData(data);
      if (!proveedor && !fechaDesde && !fechaHasta && selIds?.size === 0) {
        const provs = [...new Set(
          (data.productos || []).map(p => p.proveedor).filter(Boolean).flatMap(s => s.split(', '))
        )].sort();
        setProvDisp(provs);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingHistorico(false); }
  }, []);

  // Cargar histórico cuando se activa esa pestaña o cambia cualquier filtro
  useEffect(() => {
    if (activeTab === 'historico' && matrizId)
      loadHistorico(matrizId, clientesSel, histFiltroProveedor, histFechaDesde, histFechaHasta);
  }, [activeTab, clientesSel, histFiltroProveedor, histFechaDesde, histFechaHasta, matrizId, loadHistorico]);

  // ── Filtros ──────────────────────────────────────────────────────────────
  const filtered = productos.filter(p => {
    const s = searchFilter.toLowerCase();
    return !s || p.nombre?.toLowerCase().includes(s) ||
      p.ref_proveedor?.toLowerCase().includes(s) ||
      p.proveedores?.toLowerCase().includes(s);
  });

  const histProductosFiltrados = (historicoData?.productos || [])
    .filter(p => {
      const s = histSearch.toLowerCase();
      return !s || p.nombre?.toLowerCase().includes(s) || p.proveedor?.toLowerCase().includes(s);
    })
    .sort((a, b) => parseFloat(b[histOrden] || 0) - parseFloat(a[histOrden] || 0));

  const clientesFiltroTexto = clientesSel.size === 0
    ? 'Todas las clínicas'
    : `${clientesSel.size} clínica${clientesSel.size > 1 ? 's' : ''} seleccionada${clientesSel.size > 1 ? 's' : ''}`;

  // ── Estados de carga / error / acceso denegado ───────────────────────────
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ top: 64 }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 rounded-full animate-spin mx-auto mb-4"
            style={{ borderTopColor: P }} />
          <p className="text-gray-500">Cargando análisis de ahorros...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) return <AccesoDenegado />;

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ top: 64 }}>
        <div className="bg-white rounded-xl p-8 shadow-lg border border-red-200 text-center max-w-sm">
          <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium">{error}</p>
          <button onClick={initCliente} className="mt-4 px-4 py-2 text-white rounded-lg text-sm"
            style={{ backgroundColor: P }}>Reintentar</button>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden" style={{ top: 64, backgroundColor: '#f9fafb' }}>

      {/* ── Header blanco ──────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: P_LT }}>
            <TrendingDown size={20} style={{ color: P }} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800 leading-tight">
              Análisis de Ahorros
              {matrizInfo && <span className="font-normal text-gray-500"> — {matrizInfo.razon_social}</span>}
            </h1>
            <div className="flex items-center gap-4 mt-0.5">
              <span className="text-xs text-gray-400">
                {clientes.length} clínica{clientes.length !== 1 ? 's' : ''} · {productos.length} productos
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Card principal con tabs ────────────────────────────────────── */}
      <div className="flex-1 flex flex-col bg-white overflow-hidden min-h-0">

          {/* Tab bar */}
          <div className="flex border-b border-gray-200 bg-gray-50 flex-shrink-0">
            {[
              { key: 'precios',      label: 'Precios y Ahorros', icon: <DollarSign size={15} /> },
              { key: 'estadisticas', label: 'Estadísticas',       icon: <BarChart2  size={15} /> },
              { key: 'historico',    label: 'Ahorro Histórico',   icon: <History    size={15} /> },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className="flex items-center gap-2 px-6 py-3.5 text-sm font-medium border-b-2 transition-colors"
                style={activeTab === t.key
                  ? { borderColor: P, color: P, backgroundColor: 'white' }
                  : { borderColor: 'transparent', color: '#6b7280' }}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          {/* ══ TAB: PRECIOS (solo lectura) ══════════════════════════════ */}
          {activeTab === 'precios' && (
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">

              {/* Búsqueda */}
              <div className="p-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <SearchInput
                    className="flex-1 max-w-md"
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                    placeholder="Buscar producto, referencia, proveedor..."
                  />
                  <span className="text-sm text-gray-500 flex-shrink-0">
                    {filtered.length} producto{filtered.length !== 1 ? 's' : ''}
                  </span>
                  {/* Badge ahorro configurado */}
                  {filtered.filter(p => p.precio_anterior_matriz).length > 0 && (
                    <span className="text-xs px-2 py-1 rounded-full font-medium"
                      style={{ backgroundColor: P_LT, color: P }}>
                      {filtered.filter(p => parseFloat(p.ahorro_matriz) > 0).length} con ahorro activo
                    </span>
                  )}
                </div>
              </div>

              {/* Tabla solo lectura */}
              <div className="flex-1 overflow-auto min-h-0">
                {loadingProds ? (
                  <div className="p-12 text-center">
                    <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin mx-auto mb-3"
                      style={{ borderTopColor: P }} />
                    <p className="text-gray-400">Cargando productos...</p>
                  </div>
                ) : (
                  <table className="w-full min-w-[700px] text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gray-50 border-b border-gray-200 text-left">
                        <th className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Producto</th>
                        <th className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Proveedor</th>
                        <th className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Precio Actual</th>
                        <th className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">
                          Precio Anterior
                          <div className="font-normal normal-case text-gray-400">(antes de CS)</div>
                        </th>
                        <th className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Ahorro € / ud</th>
                        <th className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Ahorro %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filtered.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-5 py-14 text-center text-gray-400">
                            <Package size={36} className="mx-auto mb-3 opacity-30" />
                            <p className="font-medium">No se encontraron productos</p>
                            {searchFilter && (
                              <button onClick={() => setSearchFilter('')} className="mt-2 text-xs underline">
                                Limpiar búsqueda
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : filtered.map((prod, idx) => {
                        const precio     = parseFloat(prod.precio_matriz)          || null;
                        const precioAnt  = parseFloat(prod.precio_anterior_matriz) || null;
                        const ahorro     = parseFloat(prod.ahorro_matriz)          || null;
                        const ahorroPct  = precio && precioAnt && precioAnt > 0
                          ? ((precioAnt - precio) / precioAnt) * 100 : null;
                        const tieneAhorro = ahorro && ahorro > 0;

                        return (
                          <tr key={prod.id}
                            className={`hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/30'} ${tieneAhorro ? '' : ''}`}>
                            <td className="px-5 py-3">
                              <div className="font-medium text-gray-800 leading-tight">{prod.nombre}</div>
                              <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                                {prod.ref_proveedor && <span>{prod.ref_proveedor}</span>}
                                {prod.familia       && <span className="opacity-70">{prod.familia}</span>}
                                {prod.tipo_iva      && <span className="px-1 bg-gray-100 text-gray-500 rounded text-[10px]">IVA {prod.tipo_iva}%</span>}
                              </div>
                            </td>
                            <td className="px-5 py-3 text-xs text-gray-600">{prod.proveedores || '—'}</td>
                            <td className="px-5 py-3 text-right">
                              {precio !== null
                                ? <span className="font-semibold text-gray-800 text-sm">{fmt(precio)} €</span>
                                : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-5 py-3 text-right">
                              {precioAnt !== null
                                ? <span className="text-gray-500 text-sm line-through">{fmt(precioAnt)} €</span>
                                : <span className="text-gray-300 text-xs">Sin configurar</span>}
                            </td>
                            <td className="px-5 py-3 text-right">
                              {tieneAhorro ? (
                                <span className="font-bold text-green-600 text-sm">+{fmt(ahorro)} €</span>
                              ) : precioAnt ? (
                                <span className="text-gray-400 text-xs">Sin ahorro</span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3 text-right">
                              {ahorroPct !== null && ahorroPct > 0 ? (
                                <span className="inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                  <ArrowDown size={9} />{ahorroPct.toFixed(2)}%
                                </span>
                              ) : ahorroPct !== null && ahorroPct < 0 ? (
                                <span className="inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                                  <ArrowUp size={9} />{Math.abs(ahorroPct).toFixed(2)}%
                                </span>
                              ) : <span className="text-gray-300">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Nota informativa */}
              <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <p className="text-xs text-gray-400 flex items-center gap-1.5">
                  <Info size={11} />
                  Los precios anteriores reflejan lo que pagabas antes de CentralSalud. Contacta con CentralSalud para actualizarlos.
                </p>
              </div>
            </div>
          )}

          {/* ══ TAB: ESTADÍSTICAS ════════════════════════════════════════ */}
          {activeTab === 'estadisticas' && (
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              {loadingStats ? (
                <div className="text-center py-14 text-gray-400">
                  <BarChart2 size={44} className="mx-auto mb-3 opacity-30" />
                  <p>Cargando estadísticas...</p>
                </div>
              ) : !estadisticas ? (
                <div className="text-center py-14 text-gray-400">
                  <BarChart2 size={44} className="mx-auto mb-3 opacity-30" />
                  <p>Sin datos de estadísticas</p>
                </div>
              ) : (
                <div className="space-y-6">

                  {/* KPIs */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Total Productos',         value: estadisticas.summary.total_productos, sub: 'en tu catálogo', icon: <Package size={20} />, color: '#6366f1' },
                      { label: 'Con Precio Anterior',     value: estadisticas.summary.productos_con_precio_anterior, sub: 'comparativa activa', icon: <Tag size={20} />, color: P },
                      { label: 'Ahorro Total / Unidad',   value: `${parseFloat(estadisticas.summary.ahorro_total_unidad || 0).toFixed(2)} €`, sub: 'ahorro acumulado', icon: <TrendingDown size={20} />, color: '#059669' },
                      { label: 'Ahorro Promedio',         value: `${parseFloat(estadisticas.summary.ahorro_promedio_pct || 0).toFixed(2)}%`, sub: 'sobre precio anterior', icon: <Percent size={20} />, color: '#f59e0b' },
                    ].map(k => (
                      <div key={k.label} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 font-medium">{k.label}</span>
                          <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${k.color}18`, color: k.color }}>{k.icon}</div>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">{k.value}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{k.sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Distribución por rango */}
                  {estadisticas.distribucion?.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                        <BarChart2 size={16} style={{ color: P }} />
                        <h3 className="font-semibold text-gray-800">Distribución de Productos por Rango de Ahorro</h3>
                      </div>
                      <div className="p-4">
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={estadisticas.distribucion} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="rango" tick={{ fontSize: 11, fill: '#6b7280' }} />
                            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip formatter={v => `${v} productos`} />} />
                            <Bar dataKey="cantidad" name="Productos" radius={[4, 4, 0, 0]}>
                              {estadisticas.distribucion.map((d, i) => (
                                <Cell key={i} fill={RANGO_COLORS[d.rango] || P} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Top 10 por ahorro € y por proveedor */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Top por € */}
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                        <Award size={16} style={{ color: P }} />
                        <h3 className="font-semibold text-gray-800">Tus Mayores Ahorros (€/ud)</h3>
                      </div>
                      <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Antes</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ahora</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ahorro</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {estadisticas.top_ahorro.length === 0 ? (
                              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-xs">Sin comparativa configurada todavía</td></tr>
                            ) : estadisticas.top_ahorro.map(p => (
                              <tr key={p.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <div className="font-medium text-gray-800 truncate max-w-[160px]">{p.nombre}</div>
                                  <div className="text-xs text-gray-400 truncate">{p.proveedor}</div>
                                </td>
                                <td className="px-4 py-2 text-right text-xs text-gray-400 line-through">{fmt(p.precio_anterior)}</td>
                                <td className="px-4 py-2 text-right text-xs text-gray-700 font-medium">{fmt(p.precio)}</td>
                                <td className="px-4 py-2 text-right">
                                  <div className="font-bold text-green-600 text-xs">+{fmt(p.ahorro)} €</div>
                                  <div className="text-[10px] text-green-400">{parseFloat(p.ahorro_pct).toFixed(2)}%</div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Ahorro por proveedor */}
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                        <Award size={16} style={{ color: P }} />
                        <h3 className="font-semibold text-gray-800">Ahorro por Proveedor</h3>
                      </div>
                      <div className="p-4">
                        {estadisticas.ahorro_proveedores.length === 0 ? (
                          <div className="text-center py-8 text-gray-400 text-sm">Sin datos disponibles</div>
                        ) : (
                          <ResponsiveContainer width="100%" height={Math.max(160, estadisticas.ahorro_proveedores.length * 36)}>
                            <BarChart
                              layout="vertical"
                              data={estadisticas.ahorro_proveedores.map(p => ({
                                name: p.proveedor || 'Sin proveedor',
                                ahorro: parseFloat(p.ahorro_total) || 0,
                              }))}
                              margin={{ top: 4, right: 50, left: 8, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                              <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }}
                                tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(2)} />
                              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#374151' }} />
                              <Tooltip content={<CustomTooltip formatter={v => `${v.toFixed(2)} €`} />} />
                              <Bar dataKey="ahorro" name="Ahorro €" fill={P} radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Top % */}
                  {estadisticas.top_ahorro_pct?.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                        <TrendingUp size={16} style={{ color: '#6366f1' }} />
                        <h3 className="font-semibold text-gray-800">Top 10 — Productos con Mayor Descuento (%)</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left   text-xs font-medium text-gray-500">Producto</th>
                              <th className="px-4 py-2 text-right  text-xs font-medium text-gray-500">Antes</th>
                              <th className="px-4 py-2 text-right  text-xs font-medium text-gray-500">Ahora</th>
                              <th className="px-4 py-2 text-right  text-xs font-medium text-gray-500">Ahorro €</th>
                              <th className="px-4 py-2 text-right  text-xs font-medium text-gray-500">Ahorro %</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {estadisticas.top_ahorro_pct.map(p => (
                              <tr key={p.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <div className="font-medium text-gray-800 truncate max-w-[260px]">{p.nombre}</div>
                                  <div className="text-xs text-gray-400">{p.proveedor}</div>
                                </td>
                                <td className="px-4 py-2 text-right text-xs text-gray-400 line-through">{fmt(p.precio_anterior)}</td>
                                <td className="px-4 py-2 text-right text-xs text-gray-700 font-medium">{fmt(p.precio)}</td>
                                <td className="px-4 py-2 text-right"><span className="text-xs font-bold text-green-600">+{fmt(p.ahorro)} €</span></td>
                                <td className="px-4 py-2 text-right">
                                  <span className="inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                    <ArrowDown size={9} />{parseFloat(p.ahorro_pct).toFixed(2)}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Aviso si hay productos sin configurar */}
                  {estadisticas.sin_precio_anterior > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Info size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-700">
                          <strong>{estadisticas.sin_precio_anterior} producto{estadisticas.sin_precio_anterior !== 1 ? 's' : ''}</strong> aún no tienen precio anterior configurado.
                          Contacta con CentralSalud para completar la comparativa y ver el ahorro real al 100%.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══ TAB: AHORRO HISTÓRICO ════════════════════════════════════ */}
          {activeTab === 'historico' && (
            <div className="flex-1 flex overflow-hidden min-h-0">

              {/* Panel lateral — clínicas + filtros (solo si hay más de una clínica) */}
              {clientes.length > 1 && (
                <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden">

                  {/* ── Clínicas ── */}
                  <div className="p-4 border-b border-gray-200 flex-shrink-0">
                    <h4 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                      <Users size={15} style={{ color: P }} />Filtrar por clínicas
                    </h4>
                    <p className="text-xs text-gray-400 mt-0.5">Elige una o varias, o deja todas</p>
                  </div>
                  <div className="overflow-y-auto p-3 space-y-1 min-h-0" style={{ maxHeight: '220px' }}>
                    <label className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer transition-colors ${clientesSel.size === 0 ? 'bg-white shadow-sm border border-gray-200' : 'hover:bg-white'}`}>
                      <input type="radio" name="cfiltro" checked={clientesSel.size === 0}
                        onChange={() => setClientesSel(new Set())} className="w-3.5 h-3.5 accent-teal-500" />
                      <div>
                        <span className="text-sm font-medium text-gray-700">Todas</span>
                        <div className="text-xs text-gray-400">{clientes.length} clínicas</div>
                      </div>
                    </label>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider px-2 pt-2">O seleccionar:</div>
                    {clientes.map(c => {
                      const esMatriz = parseInt(c.clinica_matriz) === 0 || c.clinica_matriz === null;
                      const checked  = clientesSel.has(c.id);
                      return (
                        <label key={c.id}
                          className={`flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-white shadow-sm border' : 'hover:bg-white'}`}
                          style={checked ? { borderColor: P } : {}}>
                          <input type="checkbox" checked={checked} onChange={() => handleClienteToggle(c.id)}
                            className="w-3.5 h-3.5 mt-0.5 accent-teal-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-sm font-medium text-gray-700 truncate">{c.razon_social}</span>
                              {esMatriz && (
                                <span className="text-[9px] px-1 py-0.5 rounded font-bold flex-shrink-0"
                                  style={{ backgroundColor: P_LT, color: P }}>MTZ</span>
                              )}
                            </div>
                            {c.alias && <div className="text-xs text-gray-400 truncate">{c.alias}</div>}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* ── Proveedor ── */}
                  <div className="border-t border-gray-200 p-3 flex-shrink-0">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Filter size={10} />Proveedor
                    </p>
                    <select value={histFiltroProveedor} onChange={e => setHistProveedor(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700">
                      <option value="">Todos los proveedores</option>
                      {proveedoresDisponibles.map(pv => (
                        <option key={pv} value={pv}>{pv}</option>
                      ))}
                    </select>
                  </div>

                  {/* ── Rango de fechas ── */}
                  <div className="border-t border-gray-200 p-3 flex-shrink-0 space-y-2">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider flex items-center gap-1">
                      <Calendar size={10} />Rango de fechas
                    </p>
                    <div>
                      <label className="text-[10px] text-gray-500 mb-0.5 block">Desde</label>
                      <input type="date" value={histFechaDesde} onChange={e => setHistFechaDesde(e.target.value)}
                        max={histFechaHasta || undefined}
                        className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 mb-0.5 block">Hasta</label>
                      <input type="date" value={histFechaHasta} onChange={e => setHistFechaHasta(e.target.value)}
                        min={histFechaDesde || undefined}
                        className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700" />
                    </div>
                    {(histFiltroProveedor || histFechaDesde || histFechaHasta) && (
                      <button onClick={() => { setHistProveedor(''); setHistFechaDesde(''); setHistFechaHasta(''); }}
                        className="w-full text-xs py-1 rounded-lg border transition-colors text-center"
                        style={{ borderColor: P, color: P }}>
                        Limpiar filtros
                      </button>
                    )}
                  </div>

                  <div className="p-3 border-t border-gray-200 flex-shrink-0 text-xs text-gray-500 flex items-center gap-1.5">
                    <Filter size={11} /><span className="truncate">{clientesFiltroTexto}</span>
                  </div>
                </div>
              )}

              {/* Panel lateral — solo filtros (cuando hay una sola clínica sin sidebar de clínicas) */}
              {clientes.length === 1 && (
                <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex-shrink-0">
                    <h4 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                      <Filter size={15} style={{ color: P }} />Filtros
                    </h4>
                  </div>

                  {/* ── Proveedor ── */}
                  <div className="p-3 flex-shrink-0">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Proveedor</p>
                    <select value={histFiltroProveedor} onChange={e => setHistProveedor(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700">
                      <option value="">Todos los proveedores</option>
                      {proveedoresDisponibles.map(pv => (
                        <option key={pv} value={pv}>{pv}</option>
                      ))}
                    </select>
                  </div>

                  {/* ── Rango de fechas ── */}
                  <div className="border-t border-gray-200 p-3 flex-shrink-0 space-y-2">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider flex items-center gap-1">
                      <Calendar size={10} />Rango de fechas
                    </p>
                    <div>
                      <label className="text-[10px] text-gray-500 mb-0.5 block">Desde</label>
                      <input type="date" value={histFechaDesde} onChange={e => setHistFechaDesde(e.target.value)}
                        max={histFechaHasta || undefined}
                        className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 mb-0.5 block">Hasta</label>
                      <input type="date" value={histFechaHasta} onChange={e => setHistFechaHasta(e.target.value)}
                        min={histFechaDesde || undefined}
                        className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700" />
                    </div>
                    {(histFiltroProveedor || histFechaDesde || histFechaHasta) && (
                      <button onClick={() => { setHistProveedor(''); setHistFechaDesde(''); setHistFechaHasta(''); }}
                        className="w-full text-xs py-1 rounded-lg border transition-colors text-center"
                        style={{ borderColor: P, color: P }}>
                        Limpiar filtros
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Contenido histórico — scroll propio */}
              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                {loadingHistorico ? (
                  <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: P }} />
                    <p className="text-gray-400 text-sm">Calculando tu ahorro histórico...</p>
                  </div>
                ) : !historicoData || historicoData.productos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
                    <History size={44} className="opacity-30" />
                    <p className="font-medium text-gray-500">Aún no hay datos de ahorro histórico</p>
                    <p className="text-sm text-center max-w-sm">
                      Cuando realices pedidos de productos con precio anterior configurado,
                      aquí verás cuánto has ahorrado con CentralSalud.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">

                  {/* KPIs */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Ahorro Total Conseguido', value: fmtBigMoney(historicoData.totales.ahorro_total), sub: `desde ${fmtDate(historicoData.totales.primer_pedido)}`, icon: <TrendingDown size={20} />, color: '#059669', big: true },
                      { label: 'Unidades Compradas',      value: parseInt(historicoData.totales.total_unidades).toLocaleString('es-ES'), sub: 'con precio anterior', icon: <ShoppingCart size={20} />, color: P },
                      { label: 'Productos con Ahorro',    value: historicoData.totales.num_productos, sub: 'productos únicos', icon: <Package size={20} />, color: '#6366f1' },
                      { label: 'Ahorro Promedio/Mes',     value: fmtBigMoney(historicoData.totales.ahorro_promedio_mensual), sub: `${historicoData.por_mes.length} meses`, icon: <Calendar size={20} />, color: '#f59e0b' },
                    ].map(k => (
                      <div key={k.label}
                        className={`border rounded-xl p-4 shadow-sm ${k.big ? 'border-green-200' : 'bg-white border-gray-200'}`}
                        style={k.big ? { backgroundColor: '#f0fdf4' } : {}}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-medium ${k.big ? 'text-green-700' : 'text-gray-500'}`}>{k.label}</span>
                          <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${k.color}18`, color: k.color }}>{k.icon}</div>
                        </div>
                        <div className={`text-2xl font-bold ${k.big ? 'text-green-800' : 'text-gray-800'}`}>{k.value}</div>
                        <div className={`text-xs mt-0.5 ${k.big ? 'text-green-600' : 'text-gray-400'}`}>{k.sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Gráfica comparativa: gasto anterior vs gasto real */}
                  {historicoData.por_mes.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                          <TrendingDown size={16} style={{ color: P }} />
                          Comparativa de Gasto Mensual
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1.5">
                            <span className="inline-block w-6 border-t-2 border-dashed border-red-400" />
                            Sin CentralSalud
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span className="inline-block w-6 border-t-2 border-green-500" />
                            Tu gasto real
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mb-4">
                        El área entre las dos líneas es lo que has ahorrado ·
                        <span className="font-semibold text-green-600 ml-1">
                          Total: {parseFloat(historicoData.totales.ahorro_total || 0).toFixed(2)} €
                        </span>
                      </p>
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart
                          data={historicoData.por_mes.map(m => ({
                            mes:      fmtDate(m.mes),
                            anterior: parseFloat(m.gasto_anterior_mes) || 0,
                            real:     parseFloat(m.gasto_real_mes)     || 0,
                            ahorro:   parseFloat(m.ahorro_mes)         || 0,
                            pedidos:  parseInt(m.pedidos_mes)          || 0,
                          }))}
                          margin={{ top: 8, right: 20, left: 10, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b7280' }} />
                          <YAxis tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k €` : `${v.toFixed(0)} €`} />
                          <Tooltip content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0]?.payload;
                            return (
                              <div className="bg-gray-800 text-white text-xs px-3 py-2.5 rounded-lg shadow-lg min-w-[170px]">
                                <p className="font-semibold mb-2 border-b border-gray-600 pb-1">{label}</p>
                                <p className="flex justify-between gap-4">
                                  <span className="text-red-300">Sin CentralSalud:</span>
                                  <span className="font-medium">{parseFloat(d?.anterior || 0).toFixed(2)} €</span>
                                </p>
                                <p className="flex justify-between gap-4">
                                  <span className="text-green-300">Tu gasto real:</span>
                                  <span className="font-medium">{parseFloat(d?.real || 0).toFixed(2)} €</span>
                                </p>
                                <p className="flex justify-between gap-4 mt-1 pt-1 border-t border-gray-600">
                                  <span style={{ color: P }}>Ahorro mes:</span>
                                  <span className="font-bold text-green-400">+{parseFloat(d?.ahorro || 0).toFixed(2)} €</span>
                                </p>
                                <p className="text-gray-400 mt-1">{d?.pedidos} pedidos</p>
                              </div>
                            );
                          }} />
                          <Line
                            type="monotone" dataKey="anterior" name="Sin CentralSalud"
                            stroke="#f87171" strokeWidth={2} strokeDasharray="5 3"
                            dot={{ r: 3, fill: '#f87171' }} activeDot={{ r: 5 }} />
                          <Line
                            type="monotone" dataKey="real" name="Tu gasto real"
                            stroke="#22c55e" strokeWidth={2.5}
                            dot={{ r: 3, fill: '#22c55e' }} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Ahorro mensual — BarChart */}
                  {historicoData.por_mes.length > 1 && (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                          <Calendar size={16} style={{ color: P }} />
                          Tu Ahorro Mes a Mes
                        </h3>
                        <span className="text-xs text-gray-400">{historicoData.por_mes.length} meses</span>
                      </div>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart
                          data={historicoData.por_mes.map(m => ({
                            mes: fmtDate(m.mes),
                            ahorro: parseFloat(m.ahorro_mes) || 0,
                            pedidos: parseInt(m.pedidos_mes) || 0,
                          }))}
                          margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b7280' }} />
                          <YAxis tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0)} />
                          <Tooltip content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                                <p className="font-semibold mb-1">{label}</p>
                                <p style={{ color: P }}>Ahorro: {parseFloat(payload[0]?.value || 0).toFixed(2)} €</p>
                                <p className="text-gray-300">{payload[0]?.payload?.pedidos} pedidos</p>
                              </div>
                            );
                          }} />
                          <Bar dataKey="ahorro" name="Ahorro €" fill={P} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Ahorro por clínica (si hay varias) */}
                  {historicoData.por_cliente.length > 1 && (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                        <Users size={16} style={{ color: P }} />
                        <h3 className="font-semibold text-gray-800">Ahorro por Clínica</h3>
                      </div>
                      <div className="p-4">
                        <ResponsiveContainer width="100%" height={Math.max(140, historicoData.por_cliente.length * 44)}>
                          <BarChart
                            layout="vertical"
                            data={historicoData.por_cliente.map(c => ({
                              name: c.alias || c.razon_social,
                              ahorro: parseFloat(c.ahorro_total) || 0,
                              pedidos: c.num_pedidos,
                            }))}
                            margin={{ top: 4, right: 50, left: 8, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }}
                              tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(2)} />
                            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#374151' }} />
                            <Tooltip content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div className="bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                                  <p className="font-semibold mb-1">{label}</p>
                                  <p style={{ color: P }}>Ahorro: {parseFloat(payload[0]?.value || 0).toFixed(2)} €</p>
                                  <p className="text-gray-300">{payload[0]?.payload?.pedidos} pedidos</p>
                                </div>
                              );
                            }} />
                            <Bar dataKey="ahorro" name="Ahorro €" fill={P} radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Tabla detalle */}
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                      <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                        <Package size={16} style={{ color: P }} />
                        Detalle por Producto
                        <span className="text-xs text-gray-400 font-normal">({histProductosFiltrados.length})</span>
                      </h3>
                      <div className="flex items-center gap-2">
                        <SearchInput
                          size="sm"
                          className="w-40"
                          borderClass="border border-gray-200"
                          blurColor="#e5e7eb"
                          value={histSearch}
                          onChange={e => setHistSearch(e.target.value)}
                          placeholder="Buscar..."
                        />
                        <select value={histOrden} onChange={e => setHistOrden(e.target.value)}
                          className="border border-gray-200 rounded-lg text-xs py-1.5 px-2 focus:outline-none text-gray-600">
                          <option value="ahorro_total">Mayor ahorro €</option>
                          <option value="ahorro_pct">Mayor ahorro %</option>
                          <option value="total_unidades">Más unidades</option>
                          <option value="num_pedidos">Más pedidos</option>
                        </select>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm min-w-[760px]">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left   text-xs font-semibold text-gray-600 uppercase tracking-wide">Producto</th>
                            <th className="px-4 py-3 text-right  text-xs font-semibold text-gray-600 uppercase tracking-wide">Precio ant.</th>
                            <th className="px-4 py-3 text-right  text-xs font-semibold text-gray-600 uppercase tracking-wide">Precio pagado</th>
                            <th className="px-4 py-3 text-right  text-xs font-semibold text-gray-600 uppercase tracking-wide">Uds.</th>
                            <th className="px-4 py-3 text-right  text-xs font-semibold text-gray-600 uppercase tracking-wide">Ahorro Total</th>
                            <th className="px-4 py-3 text-right  text-xs font-semibold text-gray-600 uppercase tracking-wide">Ahorro %</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Pedidos</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Período</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {histProductosFiltrados.length === 0 ? (
                            <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Sin resultados</td></tr>
                          ) : histProductosFiltrados.map((p, idx) => (
                            <tr key={p.producto_id} className={`hover:bg-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-800">{p.nombre}</div>
                                <div className="text-xs text-gray-400 mt-0.5">
                                  {p.ref_proveedor && <span>{p.ref_proveedor}</span>}
                                  {p.proveedor && <span className="ml-1.5 text-gray-300">· {p.proveedor}</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right text-xs text-gray-400 line-through">{fmt(p.precio_anterior)} €</td>
                              <td className="px-4 py-3 text-right text-xs text-gray-700 font-medium">{fmt(p.precio_promedio_pagado)} €</td>
                              <td className="px-4 py-3 text-right font-semibold text-gray-800">
                                {parseInt(p.total_unidades).toLocaleString('es-ES')}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-bold text-green-600">+{parseFloat(p.ahorro_total).toFixed(2)} €</div>
                                <div className="text-xs text-green-400">{fmt(p.ahorro_por_unidad)} €/ud</div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                  <ArrowDown size={9} />{parseFloat(p.ahorro_pct).toFixed(2)}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center text-xs text-gray-600">{p.num_pedidos}</td>
                              <td className="px-4 py-3 text-center text-xs text-gray-400">
                                <div>{fmtDate(p.primer_pedido?.substring(0, 7))}</div>
                                <div className="text-gray-300">↓</div>
                                <div>{fmtDate(p.ultimo_pedido?.substring(0, 7))}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {histProductosFiltrados.length > 0 && (
                          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                            <tr>
                              <td className="px-4 py-3 font-bold text-gray-700" colSpan={3}>
                                TOTAL ({histProductosFiltrados.length} productos)
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-gray-800">
                                {histProductosFiltrados.reduce((s, p) => s + parseInt(p.total_unidades || 0), 0).toLocaleString('es-ES')}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-green-600 text-base">
                                +{histProductosFiltrados.reduce((s, p) => s + parseFloat(p.ahorro_total || 0), 0).toFixed(2)} €
                              </td>
                              <td colSpan={3} className="px-4 py-3 text-xs text-gray-400 text-center">{clientesFiltroTexto}</td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                </div>
                )}
              </div>{/* fin contenido histórico scroll */}
            </div>
          )}

        </div>{/* fin card tabs */}
    </div>
  );
}